import { Controller } from "@hotwired/stimulus"

interface UploadedFile {
  token: string
  filename: string
  contentType: string
  contentLength: number
}

interface PreservedFile {
  token: string
  filename: string
  content_type: string
  content_length: number
}

interface UploadSignatureResponse {
  upload_url: string
  upload_fields: Record<string, string>
  upload_token: string
  filename: string
  content_type: string
}

export default class extends Controller {
  static targets = ["dropzone", "fileInput", "fileList", "hiddenContainer", "error"]
  static values = {
    uploadUrl: String,
    partName: String,
    accept: String,
    maxFiles: { type: Number, default: 1 },
    maxFileSize: { type: Number, default: 10485760 },
    preserved: { type: Array, default: [] },
  }

  declare dropzoneTarget: HTMLElement
  declare fileInputTarget: HTMLInputElement
  declare fileListTarget: HTMLUListElement
  declare hiddenContainerTarget: HTMLElement
  declare errorTarget: HTMLElement

  declare uploadUrlValue: string
  declare partNameValue: string
  declare acceptValue: string
  declare maxFilesValue: number
  declare maxFileSizeValue: number
  declare preservedValue: PreservedFile[]

  private uploadedFiles: UploadedFile[] = []
  private activeUploads = 0

  connect() {
    this.restorePreserved()
    this.render()
  }

  // --- Actions ---

  selectFiles(event: Event) {
    event.preventDefault()
    this.fileInputTarget.click()
  }

  handleFileSelect(event: Event) {
    const input = event.target as HTMLInputElement
    if (input.files) {
      this.processFiles(Array.from(input.files))
    }
    input.value = ""
  }

  dragOver(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.add("border-primary", "bg-primary-subtle")
  }

  dragLeave(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-primary", "bg-primary-subtle")
  }

  drop(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-primary", "bg-primary-subtle")
    if (event.dataTransfer?.files) {
      this.processFiles(Array.from(event.dataTransfer.files))
    }
  }

  removeFile(event: Event) {
    event.preventDefault()
    const target = event.currentTarget as HTMLElement
    const token = target.dataset.token
    if (!token) return
    this.uploadedFiles = this.uploadedFiles.filter((f) => f.token !== token)
    this.clearError()
    this.render()
  }

  // --- Preserved values ---

  private restorePreserved() {
    for (const p of this.preservedValue) {
      // token が壊れている要素を復元すると hidden input が "undefined" になり送信時にトークンを失うため skip する
      if (typeof p.token !== "string" || p.token === "") continue
      this.uploadedFiles.push({
        token: p.token,
        filename: p.filename,
        contentType: p.content_type,
        contentLength: p.content_length,
      })
    }
  }

  // --- File processing ---

  private processFiles(files: File[]) {
    this.clearError()

    const remaining = this.maxFilesValue - this.uploadedFiles.length - this.activeUploads
    const accepted = files.slice(0, Math.max(0, remaining))

    if (files.length > accepted.length) {
      this.showError(`ファイルは最大${this.maxFilesValue}件までです。`)
    }

    for (const file of accepted) {
      if (file.size > this.maxFileSizeValue) {
        const maxMB = (this.maxFileSizeValue / (1024 * 1024)).toFixed(0)
        this.showError(`${file.name} はサイズ上限(${maxMB}MB)を超えています。`)
        continue
      }

      if (this.acceptValue && !this.isAccepted(file)) {
        this.showError(`${file.name} は許可されていないファイル形式です。`)
        continue
      }

      this.uploadFile(file)
    }
  }

  private isAccepted(file: File): boolean {
    const accepts = this.acceptValue.split(",").map((s) => s.trim().toLowerCase())
    const fileType = file.type.toLowerCase()
    const fileExt = "." + file.name.split(".").pop()?.toLowerCase()

    return accepts.some((a) => {
      if (a.startsWith(".")) {
        return fileExt === a
      }
      if (a.endsWith("/*")) {
        return fileType.startsWith(a.replace("/*", "/"))
      }
      return fileType === a
    })
  }

  // --- Upload flow ---

  private async uploadFile(file: File) {
    const uploadId = crypto.randomUUID()
    this.activeUploads++
    this.dispatchUploadingChange()
    this.addProgressItem(uploadId, file.name)
    this.updateDropzoneVisibility()

    try {
      // Step 1: Get presigned POST
      const signature = await this.fetchUploadSignature(file)

      // Step 2: Upload to S3
      await this.uploadToS3(signature, file, uploadId)

      // Step 3: Record uploaded file
      this.uploadedFiles.push({
        token: signature.upload_token,
        filename: signature.filename,
        contentType: signature.content_type,
        contentLength: file.size,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : "アップロードに失敗しました。"
      this.showError(message)
    } finally {
      this.activeUploads--
      this.dispatchUploadingChange()
      this.removeProgressItem(uploadId)
      this.render()
    }
  }

  private async fetchUploadSignature(file: File): Promise<UploadSignatureResponse> {
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
    const response = await fetch(this.uploadUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "application/json",
      },
      body: JSON.stringify({
        upload: {
          filename: file.name,
          content_type: file.type,
          content_length: file.size,
          part_name: this.partNameValue,
        },
      }),
    })

    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || "アップロードに失敗しました。")
    }

    return response.json()
  }

  private uploadToS3(signature: UploadSignatureResponse, file: File, uploadId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const formData = new FormData()

      for (const [key, value] of Object.entries(signature.upload_fields)) {
        formData.append(key, value)
      }
      formData.append("file", file)

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100)
          this.updateProgress(uploadId, percent)
        }
      })

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
        } else {
          reject(new Error("ファイルのアップロードに失敗しました。"))
        }
      })

      xhr.addEventListener("error", () => {
        reject(new Error("ネットワークエラーが発生しました。"))
      })

      xhr.addEventListener("abort", () => {
        reject(new Error("アップロードが中断されました。"))
      })

      xhr.open("POST", signature.upload_url)
      xhr.send(formData)
    })
  }

  // --- Rendering ---

  private render() {
    this.renderFileList()
    this.syncHiddenInputs()
    this.updateDropzoneVisibility()
  }

  private renderFileList() {
    // Keep progress items
    const progressItems = this.fileListTarget.querySelectorAll<HTMLElement>("[data-upload-id]")

    this.fileListTarget.innerHTML = ""

    for (const file of this.uploadedFiles) {
      const li = document.createElement("li")
      li.className = "d-flex align-items-center gap-2 py-1 px-2 bg-light rounded mb-1"

      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
      svg.setAttribute("xmlns", "http://www.w3.org/2000/svg")
      svg.setAttribute("width", "14")
      svg.setAttribute("height", "14")
      svg.setAttribute("viewBox", "0 0 24 24")
      svg.setAttribute("fill", "none")
      svg.setAttribute("stroke", "currentColor")
      svg.setAttribute("stroke-width", "2")
      svg.setAttribute("stroke-linecap", "round")
      svg.setAttribute("stroke-linejoin", "round")
      svg.classList.add("text-muted", "flex-shrink-0")
      const path1 = document.createElementNS("http://www.w3.org/2000/svg", "path")
      path1.setAttribute("d", "M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z")
      const path2 = document.createElementNS("http://www.w3.org/2000/svg", "path")
      path2.setAttribute("d", "M14 2v4a2 2 0 0 0 2 2h4")
      svg.appendChild(path1)
      svg.appendChild(path2)

      const nameSpan = document.createElement("span")
      nameSpan.className = "small text-truncate"
      nameSpan.style.maxWidth = "200px"
      nameSpan.textContent = file.filename

      const sizeSpan = document.createElement("span")
      sizeSpan.className = "small text-muted flex-shrink-0"
      sizeSpan.textContent = `(${this.formatFileSize(file.contentLength)})`

      const closeBtn = document.createElement("button")
      closeBtn.type = "button"
      closeBtn.className = "btn-close ms-auto"
      closeBtn.style.fontSize = "0.6rem"
      closeBtn.dataset.action = "click->file-upload#removeFile"
      closeBtn.dataset.token = file.token

      li.appendChild(svg)
      li.appendChild(nameSpan)
      li.appendChild(sizeSpan)
      li.appendChild(closeBtn)

      this.fileListTarget.appendChild(li)
    }

    // Re-append progress items
    for (const item of progressItems) {
      this.fileListTarget.appendChild(item)
    }
  }

  private addProgressItem(uploadId: string, filename: string) {
    const li = document.createElement("li")
    li.className = "py-1 px-2 bg-light rounded mb-1"
    li.dataset.uploadId = uploadId

    const topRow = document.createElement("div")
    topRow.className = "d-flex align-items-center gap-2 mb-1"

    const spinner = document.createElement("span")
    spinner.className = "spinner-border spinner-border-sm text-primary"
    spinner.style.width = "14px"
    spinner.style.height = "14px"

    const nameSpan = document.createElement("span")
    nameSpan.className = "small text-truncate"
    nameSpan.style.maxWidth = "200px"
    nameSpan.textContent = filename

    topRow.appendChild(spinner)
    topRow.appendChild(nameSpan)

    const progressOuter = document.createElement("div")
    progressOuter.className = "progress"
    progressOuter.style.height = "4px"

    const progressBar = document.createElement("div")
    progressBar.className = "progress-bar"
    progressBar.style.width = "0%"

    progressOuter.appendChild(progressBar)

    li.appendChild(topRow)
    li.appendChild(progressOuter)

    this.fileListTarget.appendChild(li)
  }

  private updateProgress(uploadId: string, percent: number) {
    const item = this.fileListTarget.querySelector<HTMLElement>(`[data-upload-id="${uploadId}"]`)
    if (!item) return
    const bar = item.querySelector<HTMLElement>(".progress-bar")
    if (bar) {
      bar.style.width = `${percent}%`
    }
  }

  private removeProgressItem(uploadId: string) {
    const item = this.fileListTarget.querySelector<HTMLElement>(`[data-upload-id="${uploadId}"]`)
    item?.remove()
  }

  private syncHiddenInputs() {
    this.hiddenContainerTarget.innerHTML = ""
    const inputName = `file_uploads[${this.partNameValue}][]`
    for (const file of this.uploadedFiles) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = inputName
      input.value = file.token
      this.hiddenContainerTarget.appendChild(input)
    }
  }

  private updateDropzoneVisibility() {
    if (this.uploadedFiles.length + this.activeUploads >= this.maxFilesValue) {
      this.dropzoneTarget.classList.add("d-none")
    } else {
      this.dropzoneTarget.classList.remove("d-none")
    }
  }

  // --- Error handling ---

  private showError(message: string) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("d-none")
  }

  private clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("d-none")
  }

  // --- Events ---

  private dispatchUploadingChange() {
    this.dispatch("uploading-change", {
      detail: { activeUploads: this.activeUploads },
    })
  }

  // --- Helpers ---

  private formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
}
